import { BASE_URL } from "../../api/url";
import type {
  DietType,
  IngredientActivityAction,
  IngredientActivityEntry,
} from "../Ingredient/types";
import type { DishDetail, DishIngredientRow } from "./types";
import {
  calculateCPFRatio,
  formatCPFRatio,
} from "../../Plans/Meal_Creation/CPFCalculator";

const dietTypeFromApi = (type: unknown): DietType => {
  const t = typeof type === "string" ? type.toUpperCase() : "";
  if (t === "NON_VEG" || t === "NONVEG") return "nonveg";
  if (t === "VEG") return "veg";
  return "none";
};

const resolvePictureUrl = (picture: unknown): string | null => {
  if (!picture || typeof picture !== "string") return null;
  if (/^https?:\/\//i.test(picture)) return picture;
  return `${BASE_URL}${picture.startsWith("/") ? "" : "/"}${picture}`;
};

const toNumber = (value: unknown): number => {
  const parsed =
    typeof value === "number" ? value : parseFloat(String(value ?? ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

// Cap ingredient-row numerics at 2dp and strip trailing zeros so backend
// values like "12.567890" or "12.000" render as "12.57" / "12" instead of
// overflowing the cell in the Recipe & ingredients table. Empty / missing
// values pass through so the view's "00" fallback can still kick in.
const formatRowCell = (raw: unknown): string => {
  if (raw === undefined || raw === null || raw === "") return "";
  const n = typeof raw === "number" ? raw : parseFloat(String(raw));
  if (!Number.isFinite(n) || n === 0) return "";
  return String(Number(n.toFixed(2)));
};

const formatActivityStamp = (
  isoStamp: unknown,
): { date: string; time: string } => {
  const raw = typeof isoStamp === "string" ? isoStamp : "";
  const parsed = raw ? new Date(raw) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) {
    return { date: raw, time: "" };
  }
  const date = parsed.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const time = parsed.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return { date, time };
};

// null / undefined values become empty strings so the view renders "—" rather
// than the literal "null" that String() would produce.
const parseMicronutrients = (raw: unknown): Record<string, string> => {
  if (!raw || typeof raw !== "object") return {};
  const out: Record<string, string> = {};
  Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      out[key] = "";
    } else {
      out[key] = String(value);
    }
  });
  return out;
};

// The dish detail API ships `tags` as a comma-separated string (matching the
// CREATE_DISH payload contract). Split, trim, and drop empties.
const parseTags = (raw: unknown): string[] => {
  if (Array.isArray(raw)) {
    return raw
      .map((t) => (typeof t === "string" ? t.trim() : ""))
      .filter((t) => t.length > 0);
  }
  if (typeof raw !== "string") return [];
  return raw
    .split(",")
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
};

// `hide_status` comes back as "0" / "1" (or bool). Everything non-truthy or
// literal "0" counts as active.
const isArchivedFromHideStatus = (raw: unknown): boolean => {
  if (typeof raw === "boolean") return raw;
  if (typeof raw === "number") return raw !== 0;
  if (typeof raw === "string") return raw !== "" && raw !== "0";
  return false;
};

// The dish detail response omits a top-level `created_by` string — the
// nutritionist reference is `fk_nutritionist_id`, and the display name lives
// on history entries. Pull it from the ADDED entry (falls back to the last
// entry so an edit-only trail still yields something readable).
const deriveCreatedBy = (history: any[]): string => {
  const addedEntry = history.find((h) => {
    const action = String(h?.action ?? "").toUpperCase();
    return action === "ADDED" || action === "CREATED";
  });
  const source = addedEntry ?? history[history.length - 1];
  return String(source?.performed_by ?? "");
};

const ACTIVITY_ACTION_MAP: Record<string, IngredientActivityAction> = {
  CREATED: "created",
  ADDED: "added",
  EDITED: "edited",
  UPDATED: "edited",
  HIDDEN: "archived",
  ARCHIVED: "archived",
  RESTORED: "restored",
};

// Convert the `data` payload from GET_DISH_DETAILS into the DishDetail
// shape the View + Edit dish pages consume.
export const mapApiToDishDetail = (dish: any): DishDetail => {
  const measurements: any[] = Array.isArray(dish?.measurements)
    ? dish.measurements
    : [];
  const cookingFat: any[] = Array.isArray(dish?.cooking_fat)
    ? dish.cooking_fat
    : [];
  const history: any[] = Array.isArray(dish?.history) ? dish.history : [];
  const ingredients: any[] = Array.isArray(dish?.ingredients)
    ? dish.ingredients
    : [];
  const tags = parseTags(dish?.tags);

  const baseQuantityRaw = String(dish?.dish_base_quantity ?? "").trim();
  const unitsRaw = String(dish?.dish_units ?? "").trim();
  const baseQuantity = `${baseQuantityRaw}${unitsRaw}`.trim();

  const fibreRaw = dish?.fibre;
  const fibre =
    fibreRaw === undefined || fibreRaw === null || fibreRaw === ""
      ? undefined
      : toNumber(fibreRaw);

  const dishId = toNumber(dish?.dish_id);

  /* Recipe arrives as either a plain string or an object like
     `{ recipe_id, recipe_method }` (backend variants). Pull the method
     text out either way, then split on newlines so the view can render
     a step-by-step list. */
  const recipeMethodRaw =
    typeof dish?.recipe === "string"
      ? dish.recipe
      : typeof dish?.recipe === "object" && dish?.recipe !== null
        ? (dish.recipe as { recipe_method?: string })?.recipe_method ?? ""
        : "";
  const recipeInstructions =
    typeof recipeMethodRaw === "string" && recipeMethodRaw.trim()
      ? recipeMethodRaw
          .split(/\r?\n/)
          .map((line: string) => line.trim())
          .filter((line: string) => line.length > 0)
      : [];

  /* Ingredient rows arrive with an `ingredient_` prefix on every field
     (name, quantity, units, cals, carbs, GL, AAFAdj_protein, hidden_fat).
     CPF isn't sent per row — compute it via the shared helper so the
     view can display it. Numeric fields keep their string representation
     because the view renders them as-is (with a "00" fallback). */
  const ingredientList: DishIngredientRow[] = ingredients.map((row, idx) => {
    const cals = toNumber(row?.ingredient_cals);
    const carbs = toNumber(row?.ingredient_carbs);
    const aProt = toNumber(row?.ingredient_AAFAdj_protein);
    const fats = toNumber(row?.ingredient_hidden_fat);
    const cpf = formatCPFRatio(
      calculateCPFRatio(carbs, aProt, fats, cals).ratio,
    );
    // Backend variants for the underlying food link: `fk_food_id` is the
    // canonical FK, `ingredient_id` sometimes carries the same value, and
    // `food_id` is the flattened alias. Whichever is present drives the
    // Edit-flow lookup of the ingredient's measurements + per-100g nutrition.
    const foodIdRaw =
      row?.fk_food_id ?? row?.food_id ?? row?.ingredient_id;
    const foodIdNum =
      typeof foodIdRaw === "number" ? foodIdRaw : Number(foodIdRaw);
    return {
      id: String(
        row?.ingredient_id ??
          row?.fk_food_id ??
          row?.recipe_id ??
          `${dishId}-${idx}`,
      ),
      name: String(row?.ingredient_name ?? ""),
      imageUrl: resolvePictureUrl(row?.ingredient_picture),
      quantity:
        row?.ingredient_quantity !== undefined
          ? String(row.ingredient_quantity)
          : "",
      unit: String(row?.ingredient_units ?? ""),
      cal: formatRowCell(row?.ingredient_cals),
      carb: formatRowCell(row?.ingredient_carbs),
      fats: formatRowCell(row?.ingredient_hidden_fat),
      aProt: formatRowCell(row?.ingredient_AAFAdj_protein),
      gl: formatRowCell(row?.ingredient_GL),
      // Backend doesn't ship a per-row oil value yet — leave blank so the
      // view falls back to "00".
      oil: row?.ingredient_oil !== undefined ? String(row.ingredient_oil) : "",
      cpf,
      foodId: Number.isFinite(foodIdNum) && foodIdNum > 0 ? foodIdNum : undefined,
    };
  });

  return {
    id: dishId,
    name: String(dish?.dish_name ?? ""),
    imageUrl: resolvePictureUrl(dish?.dish_image),
    createdBy: deriveCreatedBy(history),
    calories: toNumber(dish?.dish_cals),
    adjProtein: toNumber(dish?.dish_AAFAdj_protein),
    netCarbs: toNumber(dish?.net_carbs),
    fats: toNumber(dish?.dish_ingredients_fat),
    gl: toNumber(dish?.dish_gl),
    status: isArchivedFromHideStatus(dish?.hide_status) ? "Archived" : "Active",
    dietType: dietTypeFromApi(dish?.type),
    baseQuantity: baseQuantity || "—",
    // The detail endpoint doesn't yet surface separate raw / cooked values —
    // reuse the base until the backend starts sending them.
    rawQuantity: baseQuantity || "—",
    cookedQuantity: baseQuantity || "—",
    category: dish?.category ? String(dish.category) : "",
    tags,
    carbs: toNumber(dish?.dish_carbs),
    grossProtein: toNumber(dish?.dish_gross_protein),
    fiber: fibre,
    cpfRatio: String(dish?.dish_CPF ?? ""),
    micronutrients: parseMicronutrients(dish?.micronutrients),
    unitConversions: measurements.map((m, idx) => ({
      unit: String(m?.measure_unit_type ?? `Unit ${idx + 1}`),
      grams: `${parseFloat(toNumber(m?.measure_gram).toFixed(2))} g`,
    })),
    cookingFatBands: cookingFat.map((band) => ({
      minQty: String(toNumber(band?.min_qty)),
      maxQty: String(toNumber(band?.max_qty)),
      fat: String(toNumber(band?.cooking_fat_value)),
    })),
    recipeInstructions,
    ingredientList,
    activity: history.map((entry, idx): IngredientActivityEntry => {
      const rawAction = String(entry?.action ?? "").toUpperCase();
      const action: IngredientActivityAction =
        ACTIVITY_ACTION_MAP[rawAction] ?? "edited";
      const { date, time } = formatActivityStamp(entry?.performed_at);
      const remark =
        typeof entry?.remarks === "string" ? entry.remarks.trim() : "";
      return {
        id: `${dishId}-${idx}`,
        actor: String(entry?.performed_by ?? ""),
        action,
        date,
        time,
        remark: remark || undefined,
      };
    }),
  };
};
